import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

interface FaqItemProps {
  question: string;
  answer: string;
}

const faqData: FaqItemProps[] = [
  {
    question: 'O Sistema ERP da Lukos automatiza vendas online?',
    answer: 'Sim! Toda vez que uma venda acontece na loja online, marketplace ou PDV físico, o sistema sincroniza o estoque, emite a nota fiscal e já encaminha para expedição sem precisar de comandos manuais.'
  },
  {
    question: 'O PDV funciona se a internet cair?',
    answer: 'Com certeza. Nosso PDV possui contingência offline automática. Se a internet oscilar, você continua passando os produtos e emitindo cupons (NFC-e) que serão sincronizados com a SEFAZ assim que a conexão retornar.'
  },
  {
    question: 'Consigo controlar o financeiro da minha empresa com o sistema?',
    answer: 'O módulo financeiro abrange conciliação bancária, relatórios de DRE, fluxo de caixa diário e gestão completa de contas a pagar e a receber num único painel.'
  },
  {
    question: 'O sistema possui integração com maquininhas de cartão?',
    answer: 'Sim, você pode conectar as principais maquininhas do mercado (TEF) diretamente via cabo no nosso PDV desktop ou usar a nossa solução pelo celular via aproximação.'
  },
  {
    question: 'Posso testar a plataforma antes de assinar?',
    answer: 'Oferecemos 30 dias de uso completo e gratuito para você testar todas as integrações e sentir o impacto real da plataforma na rotina do seu negócio, sem exigir cartão de crédito na entrada.'
  }
];

export default function OlistFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const toggleOpen = (idx: number) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  return (
    <section className="bg-white py-24 border-t border-slate-100">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight"
          >
            Perguntas frequentes
          </motion.h2>
        </div>

        <div className="flex flex-col gap-4">
          {faqData.map((faq, idx) => {
            const isOpen = openIdx === idx;

            return (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                key={idx} 
                className={`border rounded-2xl overflow-hidden transition-colors duration-300 ${
                  isOpen ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'
                }`}
              >
                <button
                  onClick={() => toggleOpen(idx)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <h4 className="text-lg font-bold text-slate-900 pr-8">{faq.question}</h4>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    isOpen ? 'bg-purple-100 text-[#7f00ff]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>
                    {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  </div>
                </button>
                
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 pt-0 text-slate-500 leading-relaxed font-medium">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
